import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import MindmapService from "../../../services/mindmapService";

/**
 * Knowledge base → Mindmap tab.
 *
 * The mindmap is generated by a job that runs alongside course generation, so
 * three outcomes are all normal: it exists, it does not (the course was built
 * without documents — mindmaps and their citations are document-only), or it is
 * still being produced. Only a failed request is an error.
 *
 * The mindmap itself is a standalone HTML artifact in blob storage, so it opens
 * in its own tab rather than being embedded here; the panel is narrow and the
 * D3 graph needs room.
 */
const MindmapPanel = () => {
  const [status, setStatus] = useState("loading");
  const [mindmap, setMindmap] = useState(null);

  useEffect(() => {
    let cancelled = false;

    MindmapService.getTrainingMindmap()
      .then((result) => {
        if (cancelled) {
          return;
        }
        // Keyed on viewerUrl, not the blob address: the raw storage URL is
        // never opened from here. The backend composes viewerUrl from its
        // FRONTEND_URL setting — absolute, because the editor serves from a
        // different origin than the web app and could not build it itself.
        if (result && result.viewerUrl) {
          setMindmap(result);
          setStatus("ready");
        } else {
          setStatus("empty");
        }
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const renderBody = () => {
    if (status === "loading") {
      return <span>{i18n.t("Mindmap.Loading") || "Loading..."}</span>;
    }

    if (status === "error") {
      return <span>{i18n.t("Mindmap.Error") || "Could not load the mindmap."}</span>;
    }

    if (status === "empty") {
      return (
        <div style={{ textAlign: "center", lineHeight: 1.5 }}>
          <div>{i18n.t("Mindmap.Empty") || "No mindmap for this training."}</div>
          <div style={{ fontSize: "11px", marginTop: 6, color: "#A8A8A8" }}>
            {i18n.t("Mindmap.EmptyHint") || ""}
          </div>
        </div>
      );
    }

    return (
      <a
        href={mindmap.viewerUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          width: "100%",
          padding: "12px 14px",
          borderRadius: "10px",
          border: "1px solid #E2E8F0",
          background: "#F8FAFC",
          color: "#0f172a",
          textDecoration: "none",
          boxSizing: "border-box",
        }}
      >
        <span style={{ fontSize: "20px", lineHeight: 1 }}>🧠</span>
        <span style={{ textAlign: "left" }}>
          <span style={{ display: "block", fontSize: "13px", fontWeight: 600 }}>
            {i18n.t("Mindmap.Open") || "Open mindmap"}
          </span>
          <span style={{ display: "block", fontSize: "11px", color: "#64748B" }}>
            {i18n.t("Mindmap.OpenHint") || ""}
          </span>
        </span>
      </a>
    );
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        padding: "12px",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          fontSize: "14px",
          fontWeight: 600,
          color: "#0f172a",
          marginBottom: 12,
        }}
      >
        {i18n.t("Mindmap.Title") || "Mindmap"}
      </div>
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: status === "ready" ? "flex-start" : "center",
          justifyContent: "center",
          color: "#8F8F8F",
          fontSize: "13px",
        }}
      >
        {renderBody()}
      </div>
    </div>
  );
};

MindmapPanel.propTypes = {};

export default MindmapPanel;
